import { LoginAnalyticsGaService } from '@analytics/feature';
import { ChangeDetectionStrategy, Component, NgZone, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { StateUtils } from '@spartacus/core';
import { AuthMethods, FormStates, LoginForm } from '@user/const';
import { LoginService, SubmitAuthCodeFacade } from '@user/data-access';
import { BonusSystemFeatureAvailableService, LoginTypeService } from '@user/feature';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, Observable, Subscription, tap } from 'rxjs';
import { LoginFormComponentService } from '../../login-form-component.service';

@Component({
  selector: 'sd-login-by-code-form',
  templateUrl: './login-by-code-form.component.html',
  styleUrls: ['./login-by-code-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LoginByCodeFormComponent implements OnInit {
  formStates = FormStates;

  currentFormState$ = new BehaviorSubject<FormStates>(FormStates.RequestCode);
  resendTimestamp$ = new BehaviorSubject<number | null>(Date.now() + 50000);
  requestCodeState$: Observable<StateUtils.LoaderState<LoginForm>>;
  isFeatureBonusSystemAvailable$: Observable<boolean | undefined>;
  canRegister$: Observable<boolean | undefined>;

  submitSmsCodeLoading$: Observable<boolean>;

  subscription = new Subscription();

  form = this.fb.group({
    phone: this.loginFormComponentService.getPhoneControl(),
    code: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(4)]],
  });

  constructor(
    private fb: FormBuilder,
    private loginService: LoginService,
    private loginFormComponentService: LoginFormComponentService,
    private bonusSystemFeatureAvailableService: BonusSystemFeatureAvailableService,
    private submitAuthCodeFacade: SubmitAuthCodeFacade,
    private ngZone: NgZone,
    private loginAnalyticsService: LoginAnalyticsGaService,
    private loginTypeService: LoginTypeService,
  ) {}

  ngOnInit(): void {
    this.resetFormValidity();

    this.isFeatureBonusSystemAvailable$ = this.bonusSystemFeatureAvailableService.isFeatureAvailableForBaseStore();
    this.canRegister$ = this.bonusSystemFeatureAvailableService.canRegister();
    this.requestCodeState$ = this.loginService.getConfirmPhoneState().pipe(
      tap((status: StateUtils.LoaderState<LoginForm>) => {
        // после отправки смс, интерфейс не реагирует на изменение
        // этих полей, обертка ngZone.run решает эту проблему
        this.ngZone.run(() => {
          if (status.success && status.value) {
            this.resendTimestamp$.next(status.value.timestamp + status.value.codeValiditySeconds * 1000);
            this.form.controls.code.reset();
            this.currentFormState$.next(FormStates.Login);
          }
        });
      }),
    );

    this.submitSmsCodeLoading$ = this.submitAuthCodeFacade.getSubmitAuthCodeLoading();

    this.subscription.add(
      // debounceTime нужен чтобы this.login() происходил на следующем event loop, то есть уже пройдет валидация формы
      this.form.controls.code.valueChanges
        .pipe(
          filter((value) => value?.length === 4),
          debounceTime(200),
          distinctUntilChanged(),
        )
        .subscribe(() => {
          this.login();
        }),
    );
  }

  resetResendTimestamp(): void {
    this.resendTimestamp$.next(null);
  }

  resetFormValidity(): void {
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.form.updateValueAndValidity();
  }

  goBack(): void {
    this.loginService.resetConfirmPhoneState();
    this.currentFormState$.next(FormStates.RequestCode);
  }

  toggleAuthMethod(): void {
    this.loginFormComponentService.setCurrentAuthMethod(AuthMethods.ByPassword);
    this.loginAnalyticsService.sendGa('click_loginPass_btn');
  }

  requestCode(): void {
    if (!this.form.controls.phone.valid) {
      this.form.controls.phone.markAllAsTouched();

      return;
    }

    this.loginService.resetConfirmPhoneState();
    this.loginService.confirmPhone(
      '7' + this.form.controls.phone.value,
      this.loginTypeService.getDeliveryChannelByType(),
    );
  }

  login(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();

      return;
    }

    this.loginFormComponentService.login(this.form.controls.phone.value, this.form.controls.code.value as string, true);
  }
}
